// src/pages/AdminLoginForm.jsx

import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';

import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  LockOutlined as LockOutlinedIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

import api from '../api/api';
import { useAuth } from '../context/AuthContext';

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim() || '';

const AdminLoginForm = () => {
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    tempToken: '',
    otp: ''
  });

  const [verificationSetup, setVerificationSetup] = useState({
    required: false,
    qrCodeDataUrl: '',
    manualKey: '',
    showManualKey: false
  });

  const [turnstileToken, setTurnstileToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const turnstileRef = useRef(null);

  const navigate = useNavigate();
  const { login } = useAuth();

  const captchaEnabled = Boolean(TURNSTILE_SITE_KEY);

  const codeProgress = useMemo(() => {
    const len = String(formData.otp || '').length;
    return Math.min(100, Math.round((len / 6) * 100));
  }, [formData.otp]);

  const showMessage = (message, severity = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const resetCaptcha = () => {
    setTurnstileToken('');

    try {
      turnstileRef.current?.reset?.();
    } catch (_) {
      // El widget puede aún no estar renderizado.
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'otp') {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, otp: onlyDigits }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCopyManualKey = async () => {
    try {
      await navigator.clipboard.writeText(verificationSetup.manualKey || '');
      showMessage('Clave copiada.', 'success');
    } catch {
      showMessage('No se pudo copiar la clave.', 'warning');
    }
  };

  const handleSubmitCredentials = async (e) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      showMessage('Ingresa tu usuario.', 'warning');
      return;
    }

    if (!formData.password) {
      showMessage('Ingresa tu contraseña.', 'warning');
      return;
    }

    if (captchaEnabled && !turnstileToken) {
      showMessage('Completa la verificación de seguridad para continuar.', 'warning');
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/auth/login', {
        username: formData.username.trim(),
        password: formData.password,
        turnstileToken
      });

      setFormData((prev) => ({
        ...prev,
        tempToken: data.tempToken,
        otp: ''
      }));

      setVerificationSetup({
        required: Boolean(data.requiresTotpSetup),
        qrCodeDataUrl: data.qrCodeDataUrl || '',
        manualKey: data.manualKey || '',
        showManualKey: false
      });

      setStep(2);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'No fue posible iniciar sesión.';

      showMessage(msg, 'error');
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCode = async (e) => {
    e.preventDefault();

    if (!formData.otp || formData.otp.length !== 6) {
      showMessage('Ingresa el código de 6 dígitos.', 'warning');
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/auth/verify-otp', {
        tempToken: formData.tempToken,
        otp: formData.otp
      });

      login(data);
      navigate('/', { replace: true });
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Código inválido o expirado.';

      showMessage(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep(1);

    setFormData((prev) => ({
      ...prev,
      tempToken: '',
      otp: ''
    }));

    setVerificationSetup({
      required: false,
      qrCodeDataUrl: '',
      manualKey: '',
      showManualKey: false
    });

    resetCaptcha();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#07111f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.5,
        py: 2,
        background:
          'radial-gradient(circle at 20% 20%, rgba(37,99,235,0.28) 0, transparent 32%), radial-gradient(circle at 85% 22%, rgba(14,165,233,0.18) 0, transparent 30%), linear-gradient(135deg, #07111f 0%, #111827 52%, #020617 100%)'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.045,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.18) 1px, transparent 1px)',
          backgroundSize: '38px 38px'
        }}
      />

      <Paper
        elevation={18}
        sx={{
          width: '100%',
          maxWidth: 740,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '0.78fr 1.22fr'
          },
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          bgcolor: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.14)'
        }}
      >
        <Box
          sx={{
            display: {
              xs: 'none',
              md: 'flex'
            },
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 3,
            minHeight: 440,
            color: 'white',
            background:
              'linear-gradient(160deg, rgba(15,23,42,1) 0%, rgba(30,64,175,0.9) 58%, rgba(14,165,233,0.62) 100%)'
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.24)'
                }}
              >
                <SecurityIcon fontSize="small" />
              </Avatar>

              <Box>
                <Typography variant="h6" fontWeight={800}>
                  IntegraNube
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Administración
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.14)'
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Acceso reservado para usuarios autorizados.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: {
              xs: 2.5,
              sm: 3,
              md: 3.5
            }
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h5" fontWeight={900} color="#0f172a">
                    {step === 1 ? 'Iniciar sesión' : 'Verificación'}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.4, display: 'block' }}
                  >
                    {step === 1
                      ? 'Ingresa tus credenciales.'
                      : verificationSetup.required
                        ? 'Completa la configuración para continuar.'
                        : 'Ingresa el código de seguridad.'}
                  </Typography>
                </Box>

                <Avatar
                  sx={{
                    bgcolor: '#0f172a',
                    width: 38,
                    height: 38
                  }}
                >
                  <AdminPanelSettingsIcon fontSize="small" />
                </Avatar>
              </Stack>
            </Box>

            <Divider />

            {step === 1 && (
              <Box component="form" onSubmit={handleSubmitCredentials}>
                <Stack spacing={1.45}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Usuario"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    autoFocus
                    autoComplete="username"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Contraseña"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={showPassword ? 'Ocultar' : 'Mostrar'}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => setShowPassword((prev) => !prev)}
                              disabled={loading}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />

                  {captchaEnabled && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 72,
                        p: 0.75,
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden'
                      }}
                    >
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={TURNSTILE_SITE_KEY}
                        onSuccess={(token) => setTurnstileToken(token || '')}
                        onExpire={() => setTurnstileToken('')}
                        onTimeout={() => {
                          setTurnstileToken('');
                          showMessage(
                            'La verificación expiró. Intenta nuevamente.',
                            'warning'
                          );
                        }}
                        onError={() => {
                          setTurnstileToken('');
                          showMessage(
                            'No fue posible completar la verificación.',
                            'warning'
                          );
                        }}
                        options={{
                          theme: 'light',
                          size: 'flexible',
                          language: 'es',
                          action: 'login',
                          responseField: false,
                          refreshExpired: 'auto'
                        }}
                      />
                    </Box>
                  )}

                  {!captchaEnabled && (
                    <Alert severity="warning" sx={{ borderRadius: 2, py: 0.5 }}>
                      Verificación de seguridad no configurada.
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="medium"
                    disabled={loading || (captchaEnabled && !turnstileToken)}
                    sx={{
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 800,
                      textTransform: 'none',
                      boxShadow: '0 10px 22px rgba(37,99,235,0.24)'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      'Continuar'
                    )}
                  </Button>
                </Stack>
              </Box>
            )}

            {step === 2 && (
              <Box component="form" onSubmit={handleSubmitCode}>
                <Stack spacing={1.5}>
                  {verificationSetup.required && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        Escanea el código y luego ingresa el código generado.
                      </Typography>

                      {verificationSetup.qrCodeDataUrl && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 1.3,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 2
                          }}
                        >
                          <Box
                            component="img"
                            src={verificationSetup.qrCodeDataUrl}
                            alt="Código de configuración"
                            sx={{
                              width: { xs: 170, sm: 185 },
                              height: { xs: 170, sm: 185 },
                              borderRadius: 1.5
                            }}
                          />
                        </Box>
                      )}

                      {verificationSetup.manualKey && (
                        <Box>
                          {!verificationSetup.showManualKey ? (
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                setVerificationSetup((prev) => ({
                                  ...prev,
                                  showManualKey: true
                                }))
                              }
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                fontWeight: 700,
                                py: 0.65
                              }}
                            >
                              Configuración manual
                            </Button>
                          ) : (
                            <Box
                              sx={{
                                p: 1.2,
                                bgcolor: '#f1f5f9',
                                borderRadius: 2,
                                border: '1px dashed #94a3b8'
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Clave de configuración
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      mt: 0.4,
                                      display: 'block',
                                      fontFamily: 'monospace',
                                      wordBreak: 'break-all',
                                      fontWeight: 800,
                                      color: '#0f172a'
                                    }}
                                  >
                                    {verificationSetup.manualKey}
                                  </Typography>
                                </Box>

                                <Tooltip title="Copiar">
                                  <IconButton size="small" onClick={handleCopyManualKey}>
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}

                  {!verificationSetup.required && (
                    <Typography variant="caption" color="text.secondary">
                      Ingresa el código de seguridad para completar el acceso.
                    </Typography>
                  )}

                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Código de seguridad"
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      required
                      autoFocus
                      disabled={loading}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 6,
                        style: {
                          textAlign: 'center',
                          letterSpacing: '0.32em',
                          fontSize: '1.25rem',
                          fontWeight: 900
                        }
                      }}
                      autoComplete="one-time-code"
                    />

                    <LinearProgress
                      variant="determinate"
                      value={codeProgress}
                      sx={{
                        mt: 0.8,
                        height: 4,
                        borderRadius: 999,
                        bgcolor: '#e2e8f0'
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="medium"
                    disabled={loading || formData.otp.length !== 6}
                    sx={{
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 800,
                      textTransform: 'none',
                      boxShadow: '0 10px 22px rgba(37,99,235,0.24)'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      'Ingresar'
                    )}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    size="small"
                    disabled={loading}
                    onClick={handleBackToCredentials}
                    startIcon={<ArrowBackIcon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" textAlign="center">
              Acceso restringido.
            </Typography>
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminLoginForm;